import { useCallback, useEffect, useState } from "react";
import { Cpu, Wallet, ArrowDownLeft, ArrowUpRight, CreditCard, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { fmtAmount } from "@/lib/render";
import { useAuth } from "@/context/AuthContext";
import { EngineForm } from "@/components/processor-engine/EngineForm";
import { EngineRecords } from "@/components/processor-engine/EngineRecords";
import { LimitsCard, StatementCard } from "@/components/processor-engine/EngineControls";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Icon className="h-4 w-4" />{label}</div>
    <p data-testid={testid} className="mt-2 font-mono text-2xl font-semibold">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const VAULT_SNIPPET = `curl -X POST $CITIDAN_URL/api/v1/processor/charges \\
  -H "X-API-Key: $VAULT_API_KEY" -H "Content-Type: application/json" \\
  -d '{"amount": 25, "currency": "USD", "merchant_reference": "VLT-1001",
       "depositor": {"full_name": "Jane Doe", "email": "jane@example.com"},
       "card": {"cardholder_name": "Jane Doe", "card_number": "4242424242424242",
                "expiry_month": 12, "expiry_year": 2030, "cvv": "123"}}'`;

export default function ProcessorEnginePage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState(null);

  const load = useCallback(() => {
    api.get("/processor-engine/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/processor-engine/records").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const t = summary?.totals || {};
  return (
    <div className="space-y-6" data-testid="processor-engine-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Cpu className="h-6 w-6 text-indigo-400" /> CITIDAN Processor Engine
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Card & bank deposits and withdrawals, posted to the ledger in real time. Account ID{" "}
          <code data-testid="pe-account-id" className="font-mono text-foreground">{summary?.account?.id || "citidan-processor"}</code>
        </p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={Wallet} label="Processor balance" testid="pe-balance" value={summary ? fmtAmount(summary.account?.balance, "USD") : "—"} sub="CITIDAN Processor · USD" />
        <Stat icon={ArrowDownLeft} label="Deposits" testid="pe-total-deposit" value={fmtAmount(t.deposit?.total || 0, "USD")} sub={`${t.deposit?.count || 0} records`} />
        <Stat icon={ArrowUpRight} label="Withdrawals" testid="pe-total-withdrawal" value={fmtAmount(t.withdrawal?.total || 0, "USD")} sub={`${t.withdrawal?.count || 0} records`} />
        <Stat icon={CreditCard} label="Vault card charges" testid="pe-total-charge" value={fmtAmount(t.charge?.total || 0, "USD")} sub={`${t.charge?.count || 0} approved`} />
        <Stat icon={Undo2} label="Refunds" testid="pe-total-refund" value={fmtAmount(t.refund?.total || 0, "USD")} sub={`${t.refund?.count || 0} refunds`} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-3"><EngineForm onDone={load} /></div>
        <div className="xl:col-span-2 space-y-6">
        <LimitsCard limits={summary?.limits} isAdmin={user?.role === "admin"} onDone={load} />
        <StatementCard />
        <div className="rounded-xl border border-border bg-card p-5 space-y-3" data-testid="pe-vault-guide">
          <p className="font-display font-semibold">Vault app → card charge</p>
          <p className="text-sm text-muted-foreground">
            Create a <span className="text-foreground">write</span> API key under API Keys and give it to the Vault app.
            Charges are approved instantly, credited to the Processor account and returned with a TXHS.
            Re-sending the same <code className="font-mono">merchant_reference</code> returns the original charge.
          </p>
          <pre className="rounded-lg bg-muted/50 border border-border p-3 text-[11px] font-mono whitespace-pre-wrap break-all">{VAULT_SNIPPET}</pre>
          <p className="text-xs text-muted-foreground">Status: <code className="font-mono">GET /api/v1/processor/charges/{"{id}"}</code> · webhook events <code className="font-mono">charge.approved</code>, <code className="font-mono">charge.refunded</code></p>
        </div>
        </div>
      </div>
      <EngineRecords rows={rows} isAdmin={user?.role === "admin"} onChanged={load} />
    </div>
  );
}
